import { useState } from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  PageShell,
  Pagination,
  SectionCard,
  Skeleton,
  StatusBadge,
  toast,
  type DataTableColumn,
} from '@/components/ui';
import { ExportJobError, formatBytes } from '@/lib/exportJobs';
import { AdminNameCell } from '@/features/admin/components/AdminNameCell';
import {
  downloadProductExportFile,
  useProductExportJobsQuery,
  type ProductExportJob,
} from './productExportJobs';

const PAGE_SIZE = 20;

const KST = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const kst = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso.replace(/(\.\d{3})\d+/, '$1'));
  if (Number.isNaN(d.getTime())) return null;
  const p = KST.formatToParts(d);
  const v = (t: Intl.DateTimeFormatPartTypes) => p.find((x) => x.type === t)?.value ?? '';
  return `${v('year')}-${v('month')}-${v('day')} ${v('hour')}:${v('minute')}`;
};

const STATUS_VARIANT: Record<string, 'pending' | 'progress' | 'success' | 'danger' | 'neutral'> = {
  PENDING: 'pending',
  RUNNING: 'progress',
  COMPLETED: 'success',
  FAILED: 'danger',
  EXPIRED: 'neutral',
};
const STATUS_LABEL: Record<string, string> = {
  PENDING: '대기 중',
  RUNNING: '생성 중',
  COMPLETED: '완료',
  FAILED: '실패',
  EXPIRED: '보관 만료',
};
const TYPE_LABEL: Record<string, string> = { PRODUCT: '상품 엑셀', PRODUCT_FULL: '전체필드 엑셀' };

const errorText = (err: unknown, fallback: string): string =>
  err instanceof ExportJobError && err.showBeMessage ? err.message : fallback;

/**
 * 상품 엑셀 다운로드 내역 — 내 요청, 진행 중이면 자동 새로고침, 받기. 다운로드 이력은 로그라 삭제하지 않는다(QA-332,
 * 보관 기한이 지나면 파일만 만료). 요청자 이름 표시. 헤더는 가운데, 칸 너비는 내용에 맞춤.
 */
export const ProductExportJobsPage = () => {
  const { t } = useTranslation('products');
  const [page, setPage] = useState(1);
  const query = useProductExportJobsQuery(page, PAGE_SIZE);
  const [downloading, setDownloading] = useState<string | null>(null);

  const download = async (job: ProductExportJob) => {
    setDownloading(job.exportJobId);
    try {
      await downloadProductExportFile(job.exportJobId, job.fileName ?? 'products.xlsx');
    } catch (err) {
      toast.error(errorText(err, t('products.exportJobs.downloadError', '파일을 받지 못했습니다.')));
    } finally {
      setDownloading(null);
    }
  };

  const columns: DataTableColumn<ProductExportJob>[] = [
    {
      key: 'requestedAt',
      header: t('products.exportJobs.col.requestedAt', '요청일시'),
      width: 160,
      align: 'center',
      cell: (j) => <span className="whitespace-nowrap tabular-nums">{kst(j.requestedAt) ?? '—'}</span>,
    },
    {
      key: 'requestedBy',
      header: t('products.exportJobs.col.requestedBy', '요청자'),
      width: 120,
      align: 'center',
      cell: (j) => <AdminNameCell adminUserId={j.requestedBy} />,
    },
    {
      key: 'type',
      header: t('products.exportJobs.col.type', '종류'),
      width: 120,
      align: 'center',
      cell: (j) => t(`products.exportJobs.type.${j.exportType}`, TYPE_LABEL[j.exportType] ?? j.exportType),
    },
    {
      key: 'status',
      header: t('products.exportJobs.col.status', '상태'),
      width: 100,
      align: 'center',
      cell: (j) => (
        <div className="flex flex-col items-center gap-1 whitespace-nowrap">
          <StatusBadge
            variant={STATUS_VARIANT[j.status] ?? 'neutral'}
            label={t(`products.exportJobs.status.${j.status}`, STATUS_LABEL[j.status] ?? j.status)}
          />
          {j.status === 'FAILED' && j.errorMessage ? (
            <span className="text-[12px]" style={{ color: 'var(--status-danger-fg)' }}>
              {j.errorMessage}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'size',
      header: t('products.exportJobs.col.size', '파일 크기'),
      width: 100,
      align: 'center',
      cell: (j) => <span className="tabular-nums">{j.fileSizeBytes != null ? formatBytes(j.fileSizeBytes) : '—'}</span>,
    },
    {
      key: 'expiresAt',
      header: t('products.exportJobs.col.expiresAt', '보관 기한'),
      width: 160,
      align: 'center',
      cell: (j) => <span className="whitespace-nowrap tabular-nums">{kst(j.expiresAt) ?? '—'}</span>,
    },
    {
      key: 'download',
      header: t('products.exportJobs.col.download', '받기'),
      width: 100,
      align: 'center',
      cell: (j) =>
        j.status === 'COMPLETED' ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="justify-start"
            disabled={downloading === j.exportJobId}
            onClick={() => void download(j)}
            title={j.fileName ?? undefined}
            data-testid="product-export-job-download"
          >
            <Download size={14} aria-hidden="true" />
            {t('products.exportJobs.download', '받기')}
          </Button>
        ) : (
          '—'
        ),
    },
  ];

  const total = query.data?.meta?.pagination?.totalItems ?? query.data?.items.length ?? 0;

  return (
    <PageShell
      title={t('products.exportJobs.title', '상품 엑셀 다운로드 내역')}
      description={t(
        'products.exportJobs.description',
        '상품 목록에서 요청한 엑셀 파일을 받습니다. 생성 중인 요청은 자동으로 새로고침되며, 완료된 파일은 7일 동안 보관됩니다.',
      )}
      backHref="/products"
      backLabel={t('products.list.title', '상품 관리')}
    >
      <SectionCard padded={false}>
        {query.isPending ? (
          <Skeleton className="m-4 h-24" />
        ) : query.isError ? (
          <p role="alert" className="p-4 text-[13px]" style={{ color: 'var(--status-danger-fg)' }}>
            {errorText(query.error, t('products.exportJobs.loadError', '다운로드 내역을 불러오지 못했습니다.'))}
          </p>
        ) : (
          <DataTable
            columns={columns}
            rows={query.data.items}
            rowKey={(j) => j.exportJobId}
            ariaLabel={t('products.exportJobs.title', '상품 엑셀 다운로드 내역')}
            emptyState={t('products.exportJobs.empty', '요청한 엑셀이 없습니다. 상품 목록의 엑셀 일괄 작업에서 요청하세요.')}
          />
        )}
      </SectionCard>
      {total > PAGE_SIZE ? (
        <Pagination total={total} currentPage={page} pageSize={PAGE_SIZE} onPageChange={setPage} />
      ) : null}
    </PageShell>
  );
};
